import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Correct KPI data
const KPI_DATA = [
  { key: "projects", value: "135", label: "Projets", sublabel: "réalisés en Suisse" },
  { key: "cantons", value: "4", label: "Cantons", sublabel: "Vaud · Fribourg · Berne · Valais" },
  { key: "financing", value: "534", label: "millions", sublabel: "de francs suisses en transactions" },
  { key: "experience", value: "20", label: "Années", sublabel: "d'expertise reconnue" },
];

// Single digit with vertical scroll effect
function ScrollDigit({ digit, delay }) {
  const isNumber = /[0-9]/.test(digit);
  
  if (!isNumber) {
    return (
      <motion.span
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        {digit}
      </motion.span>
    );
  }

  const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  const targetIndex = parseInt(digit);

  return (
    <span className="inline-block h-[1em] overflow-hidden relative">
      <motion.span
        className="flex flex-col"
        initial={{ y: "0%" }}
        animate={{ y: `${-targetIndex * 10}%` }}
        transition={{
          delay,
          duration: 1.2,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {numbers.map((num) => (
          <span key={num} className="h-[1em] flex items-center justify-center">
            {num}
          </span>
        ))}
      </motion.span>
    </span>
  );
}

// Animated value display
function AnimatedValue({ value, delay }) {
  return (
    <span className="inline-flex">
      {value.split('').map((char, i) => (
        <ScrollDigit key={i} digit={char} delay={delay + i * 0.1} />
      ))}
    </span>
  );
}

// Single KPI fullscreen display
function KpiDisplay({ kpi, isActive }) {
  return (
    <AnimatePresence mode="wait">
      {isActive && (
        <motion.div
          key={kpi.key}
          className="absolute inset-0 flex flex-col items-center justify-center px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Main value */}
          <motion.div
            className="text-[120px] sm:text-[160px] md:text-[200px] lg:text-[280px] font-extralight text-white leading-none tracking-tighter"
            initial={{ opacity: 0, scale: 0.8, filter: "blur(20px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <AnimatedValue value={kpi.value} delay={0.2} />
          </motion.div>

          {/* Label */}
          <motion.div
            className="mt-4 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-2xl sm:text-3xl md:text-4xl font-light text-[#B07D62] tracking-wide uppercase">
              {kpi.label}
            </p>
            <motion.p
              className="mt-2 text-sm sm:text-base text-[#415A77]/70 tracking-wide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              {kpi.sublabel}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Progress dots
function ProgressDots({ current, total }) {
  return (
    <div className="flex gap-3">
      {Array.from({ length: total }).map((_, i) => (
        <motion.div
          key={i}
          className="relative"
          initial={{ opacity: 0.3 }}
          animate={{ opacity: i <= current ? 1 : 0.3 }}
          transition={{ duration: 0.3 }}
        >
          <div className={`w-2 h-2 rounded-full ${i <= current ? 'bg-[#B07D62]' : 'bg-white/20'} transition-colors duration-500`} />
          {i === current && (
            <motion.div
              className="absolute inset-0 rounded-full bg-[#B07D62]"
              initial={{ scale: 1 }}
              animate={{ scale: 2, opacity: 0 }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          )}
        </motion.div>
      ))}
    </div>
  );
}

export default function LoadingScreen({ onLoadingComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timers = [];
    
    // Show each KPI for 2 seconds
    KPI_DATA.forEach((_, index) => {
      if (index > 0) {
        timers.push(setTimeout(() => setCurrentIndex(index), index * 2000));
      }
    });

    // Start exit after all KPIs shown
    timers.push(setTimeout(() => setIsExiting(true), KPI_DATA.length * 2000 + 500));
    
    // Complete loading
    timers.push(setTimeout(() => {
      if (onLoadingComplete) onLoadingComplete();
    }, KPI_DATA.length * 2000 + 1200));

    return () => timers.forEach(clearTimeout);
  }, [onLoadingComplete]);

  if (isExiting) {
    return (
      <motion.div
        className="fixed inset-0 z-[200] bg-[#030303]"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.8 }}
      />
    );
  }

  return (
    <motion.div
      className="fixed inset-0 z-[200] bg-[#030303] overflow-hidden"
      initial={{ opacity: 1 }}
    >
      {/* Animated background layers */}
      <div className="absolute inset-0">
        {/* Central glow */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(74,144,255,0.08) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Rotating ring */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/[0.03]"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Second rotating ring */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-[#B07D62]/[0.05]"
          animate={{ rotate: -360 }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        />

        {/* Floating particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-[#B07D62]/30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: Math.random() * 5 + 5,
              delay: Math.random() * 3,
              repeat: Infinity,
            }}
          />
        ))}
      </div>

      {/* Logo at top - centered */}
      <motion.div
        className="absolute top-8 md:top-12 left-0 right-0 flex justify-center z-20"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <img src="/logo-jd.png" alt="JD" className="h-20 md:h-24 w-auto" />
      </motion.div>

      {/* KPI displays */}
      <div className="relative w-full h-full">
        {KPI_DATA.map((kpi, index) => (
          <KpiDisplay
            key={kpi.key}
            kpi={kpi}
            isActive={index === currentIndex}
          />
        ))}
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-6 z-20">
        {/* Progress dots */}
        <ProgressDots current={currentIndex} total={KPI_DATA.length} />
        
        {/* Tagline */}
        <motion.p
          className="text-xs text-[#415A77]/40 tracking-widest uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Expert en immobilier · Suisse romande
        </motion.p>
      </div>

      {/* Corner accents */}
      <motion.div
        className="absolute top-6 left-6 w-12 h-12 border-l border-t border-white/[0.06]"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      />
      <motion.div
        className="absolute bottom-6 right-6 w-12 h-12 border-r border-b border-white/[0.06]"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      />
    </motion.div>
  );
}
